import type { PlaycastDefaultProfiles, PlaycastPeerClientMetadata, PlaycastPeerMediaMetadata } from '@playcastdotio/CoreTypes';
import type {
  PlaycastMessage,
  PlaycastMessagePeerChangeRequestedProfile,
  PlaycastMessagePeerClose,
  PlaycastMessagePeerFrameratePanic,
  PlaycastMessagePeerHandshake,
  PlaycastMessagePeerHandshakeReply,
  PlaycastMessagePeerUpdateSessionDescription,
  PlaycastMessagePeerUpdateTrackMetadata,
  PlaycastMessageSource,
  SignalingGenerationV1,
} from '../types';
import { formatMessage } from './_util/formatMessage';
const target = 'peer';

/**
 * Builds a valid generation envelope for signaling message fixtures and clients.
 *
 * Rejects inputs the wire schema would refuse rather than emitting an envelope that cannot
 * validate: a blank `sessionId` or a negative / non-integer `epoch` throws.
 */
export const signalingGeneration = (sessionId: string, epoch: number): SignalingGenerationV1 => {
  if (typeof sessionId !== 'string' || sessionId.trim().length === 0) {
    throw new Error('signalingGeneration: sessionId must contain a non-whitespace character');
  }
  if (!Number.isInteger(epoch) || epoch < 0) {
    throw new Error('signalingGeneration: epoch must be a non-negative integer');
  }
  return {
    version: 1,
    sessionId,
    epoch,
  };
};

export const handshake = (
  source: PlaycastMessageSource,
  hostId: string,
  metadata: PlaycastPeerClientMetadata,
  userAuth?: string,
): PlaycastMessage<PlaycastMessagePeerHandshake> => {
  const message: PlaycastMessagePeerHandshake = {
    target,
    action: 'handshake',
    isReply: false,
    message: metadata,
  };
  return formatMessage(hostId, source, message, userAuth) as PlaycastMessage<PlaycastMessagePeerHandshake>;
};

export const handshakeReply = (
  source: PlaycastMessageSource,
  hostId: string,
  valid: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessagePeerHandshakeReply> => {
  const message: PlaycastMessagePeerHandshakeReply = {
    target,
    action: 'handshake',
    isReply: true,
    message: {
      valid,
    },
  };
  return formatMessage(hostId, source, message, userAuth) as PlaycastMessage<PlaycastMessagePeerHandshakeReply>;
};

export const updateSessionDescription = (
  source: PlaycastMessageSource,
  hostId: string,
  targetPeerId: string,
  sessionDescription: RTCSessionDescriptionInit,
  userAuth?: string,
): PlaycastMessage<PlaycastMessagePeerUpdateSessionDescription> => {
  const message: PlaycastMessagePeerUpdateSessionDescription = {
    target,
    action: 'updateSessionDescription',
    isReply: false,
    message: {
      targetPeerId,
      sessionDescription,
    },
  };
  return formatMessage(hostId, source, message, userAuth) as PlaycastMessage<PlaycastMessagePeerUpdateSessionDescription>;
};

export const updateTrackMetadata = (
  source: PlaycastMessageSource,
  hostId: string,
  targetPeerId: string,
  trackMetadata: { [trackId: string]: PlaycastPeerMediaMetadata },
  userAuth?: string,
): PlaycastMessage<PlaycastMessagePeerUpdateTrackMetadata> => {
  const message: PlaycastMessagePeerUpdateTrackMetadata = {
    target,
    action: 'updateTrackMetadata',
    isReply: false,
    message: {
      targetPeerId,
      metadata: trackMetadata,
    },
  };
  return formatMessage(hostId, source, message, userAuth) as PlaycastMessage<PlaycastMessagePeerUpdateTrackMetadata>;
};

export const changeRequestedProfile = (
  source: PlaycastMessageSource,
  hostId: string,
  profile: PlaycastDefaultProfiles,
  userAuth?: string,
): PlaycastMessage<PlaycastMessagePeerChangeRequestedProfile> => {
  const message: PlaycastMessagePeerChangeRequestedProfile = {
    target,
    action: 'changeRequestedProfile',
    isReply: false,
    message: {
      profile,
    },
  };
  return formatMessage(hostId, source, message, userAuth) as PlaycastMessage<PlaycastMessagePeerChangeRequestedProfile>;
};

export const frameratePanic = (
  source: PlaycastMessageSource,
  hostId: string,
  hardReset: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessagePeerFrameratePanic> => {
  const message: PlaycastMessagePeerFrameratePanic = {
    target,
    action: 'frameratePanic',
    isReply: false,
    message: {
      hardReset,
    },
  };
  return formatMessage(hostId, source, message, userAuth) as PlaycastMessage<PlaycastMessagePeerFrameratePanic>;
};

export const close = (
  source: PlaycastMessageSource,
  hostId: string,
  reason?: string,
  userAuth?: string,
): PlaycastMessage<PlaycastMessagePeerClose> => {
  const message: PlaycastMessagePeerClose = {
    target,
    action: 'close',
    isReply: false,
    message: {
      reason,
    },
  };
  return formatMessage(hostId, source, message, userAuth) as PlaycastMessage<PlaycastMessagePeerClose>;
};
